import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  useFinancialData, 
  useClientsData, 
  usePackagesData, 
  useReservationsData,
  useSuppliersData,
  useDashboardData
} from '../../services/dataService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Menu,
  MenuList,
  MenuItem as MenuListItem,
  Popover,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  ListItemAvatar,
  Autocomplete,
  Skeleton,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  DateRange as DateRangeIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  HourglassBottom as HourglassBottomIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Assignment as AssignmentIcon,
  Insights as InsightsIcon,
  CardTravel as TravelIcon,
  PersonPin as PersonPinIcon,
  Sync as SyncIcon,
  Flight as FlightIcon
} from '@mui/icons-material';

// Chart visualization components (simulated charts with visual representations)
const SimpleLineChart = ({ data, color = '#1976d2' }) => (
  <Box sx={{ position: 'relative', height: 300, bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', top: 8, left: 16 }}>
      Évolution des Revenus
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'end', height: '85%', gap: 1, pt: 3 }}>
      {data.data.map((value, index) => (
        <Box
          key={index}
          sx={{
            flex: 1,
            height: `${(value / Math.max(...data.data, 1)) * 100}%`,
            bgcolor: color,
            borderRadius: '4px 4px 0 0',
            minHeight: '10px',
            opacity: 0.8,
            transition: 'all 0.3s ease',
            '&:hover': { 
              opacity: 1, 
              transform: 'scaleY(1.05)',
              boxShadow: '0 0 8px rgba(25, 118, 210, 0.5)'
            }
          }}
        />
      ))}
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
      {data.labels.map((label, index) => (
        <Typography key={index} variant="caption" color="text.secondary">
          {label}
        </Typography>
      ))}
    </Box>
    {/* Add value indicators */}
    <Box sx={{ position: 'absolute', top: 8, right: 16 }}>
      <Typography variant="caption" color="text.secondary">
        Max: {Math.max(...data.data).toLocaleString()} DZD
    </Typography>
    </Box>
  </Box>
);

// Removed SimplePieChart component as it's no longer needed

const Finances = () => {
  const { formatCurrency, settings, calculateTotalWithTax } = useSettings();
  const navigate = useNavigate();
  
  // Use data services for real-time interconnected data
  const { transactions, loading: transactionsLoading, error: transactionsError, stats: financialStats } = useFinancialData();
  const { clients, loading: clientsLoading } = useClientsData();
  const { packages, loading: packagesLoading } = usePackagesData();
  const { reservations, loading: reservationsLoading } = useReservationsData();
  const { suppliers, loading: suppliersLoading } = useSuppliersData();
  const { dashboardData, loading: dashboardLoading } = useDashboardData();
  

  
  // Combined loading state
  const isLoading = transactionsLoading || clientsLoading || packagesLoading || reservationsLoading || suppliersLoading || dashboardLoading;
  
  // Calculate total commissions from transactions
  const totalCommissions = transactions ? transactions
    .filter(t => t.type === 'Recette' && t.commission)
    .reduce((sum, t) => sum + (t.commission || 0), 0) : 0;
  
  // Get interconnected clients with financial activity
  const interconnectedClients = useMemo(() => {
    if (!clients || !transactions) return [];
    
    // Get unique client IDs from transactions
    const clientIdsWithTransactions = [...new Set(
      transactions
        .filter(t => t.clientId)
        .map(t => t.clientId)
    )];
    
    // Return client details for clients with transactions
    return clients.filter(client => 
      clientIdsWithTransactions.includes(client.id)
    );
  }, [clients, transactions]);
  
  // Get interconnected reservations with financial activity
  const interconnectedReservations = useMemo(() => {
    if (!reservations || !transactions) return [];
    
    // Get unique reservation IDs from transactions
    const reservationIdsWithTransactions = [...new Set(
      transactions
        .filter(t => t.reservationId)
        .map(t => t.reservationId)
    )];
    
    // Return reservation details for reservations with transactions
    return reservations.filter(reservation => 
      reservationIdsWithTransactions.includes(reservation.id)
    );
  }, [reservations, transactions]);
  
  // Get monthly revenue data for chart
  const monthlyRevenueData = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return {
        data: [0, 0, 0, 0, 0, 0],
        labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin']
      };
    }
    
    const now = new Date();
    let startDate = new Date();
    let interval = 'month';
    let dataPoints = 6;
    let labels = [];
    
    // Default to 6 months
    startDate.setMonth(now.getMonth() - 5);
    dataPoints = 6;
    interval = 'month';
    
    // Create array of time periods
    const periods = [];
    const periodDates = [];
    
    if (interval === 'month') {
      // Monthly intervals
      for (let i = 0; i < dataPoints; i++) {
        const date = new Date(startDate);
        date.setMonth(startDate.getMonth() + i);
        periods.push(date.getMonth());
        
        // Create month labels
        const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
        labels.push(monthNames[date.getMonth()]);
        periodDates.push(date);
      }
    }
    
    // Calculate revenue for each period
    const data = periods.map((period, index) => {
      return transactions
        .filter(t => {
          const transactionDate = new Date(t.date);
          if (t.type !== 'Recette') return false;
          
          // Filter by month
          return transactionDate.getMonth() === period && 
                transactionDate.getFullYear() === periodDates[index].getFullYear();
        })
        .reduce((sum, t) => sum + (t.montant || 0), 0);
    });
    
    return { data, labels };
  }, [transactions]);
  
  // Removed packageTypeDistribution calculation as it's no longer needed
  
  const [activeTab, setActiveTab] = useState(0);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Revenue chart filter
  const [revenueChartPeriod, setRevenueChartPeriod] = useState('6months');
  
  // Add notification system for real-time updates
  const [updateNotification, setUpdateNotification] = useState({
    show: false,
    source: '',
    message: ''
  });

  const [formData, setFormData] = useState({
    type: 'Recette',
    categorie: 'Réservation',
    clientId: '',
    client: '',
    description: '',
    montant: 0,
    devise: 'DZD',
    showConversion: false, // To control visibility of conversion fields
    montantDZD: 0,         // Equivalent amount in DZD
    tauxDeChange: null,    // Exchange rate used
    typeTauxDeChange: 'sell', // 'buy' or 'sell'
    methode: 'Virement bancaire',
    statut: 'En attente',
    notes: '',
    enableTVA: true,
    customTVARate: settings.taxRate,
    enableCommission: true,
    customCommissionRate: 10,
    date: new Date().toISOString().split('T')[0]
  });
  
  // Add state for details dialog
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [detailsData, setDetailsData] = useState(null);

  // Add a separate state for filtered chart data
  const [filteredChartData, setFilteredChartData] = useState({
    data: [0, 0, 0, 0, 0, 0],
    labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin']
  });

  // Apply chart period filter
  useEffect(() => {
    if (!transactions || transactions.length === 0) {
      setFilteredChartData({
        data: [0, 0, 0, 0, 0, 0],
        labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin']
      });
      return;
    }

    const now = new Date();
    let startDate = new Date();
    let interval = 'month';
    let dataPoints = 6;
    let labels = [];
    
    // Set start date and interval based on selected period
    switch (revenueChartPeriod) {
      case 'year':
        // Last 12 months
        startDate.setMonth(now.getMonth() - 11);
        dataPoints = 12;
        interval = 'month';
        break;
      case '6months':
        // Last 6 months
        startDate.setMonth(now.getMonth() - 5);
        dataPoints = 6;
        interval = 'month';
        break;
      case 'month':
        // Last 4 weeks
        startDate.setDate(now.getDate() - 28);
        dataPoints = 4;
        interval = 'week';
        break;
      case 'week':
        // Last 7 days
        startDate.setDate(now.getDate() - 6);
        dataPoints = 7;
        interval = 'day';
        break;
      default:
        // Default to 6 months
        startDate.setMonth(now.getMonth() - 5);
        dataPoints = 6;
        interval = 'month';
    }
    
    // Create array of time periods
    const periods = [];
    const periodDates = [];
    
    if (interval === 'month') {
      // Monthly intervals
      for (let i = 0; i < dataPoints; i++) {
        const date = new Date(startDate);
        date.setMonth(startDate.getMonth() + i);
        periods.push(date.getMonth());
        
        // Create month labels
        const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
        labels.push(monthNames[date.getMonth()]);
        periodDates.push(date);
      }
    } else if (interval === 'week') {
      // Weekly intervals
      for (let i = 0; i < dataPoints; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + (i * 7));
        periods.push(i);
        
        // Create week labels (e.g., "S1", "S2", etc.)
        labels.push(`S${i+1}`);
        periodDates.push(date);
      }
    } else {
      // Daily intervals
      for (let i = 0; i < dataPoints; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        periods.push(date.getDate());
        
        // Create day labels (e.g., "Lun", "Mar", etc.)
        const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        labels.push(dayNames[date.getDay()]);
        periodDates.push(date);
      }
    }
    
    // Calculate revenue for each period
    const data = periods.map((period, index) => {
      return transactions
        .filter(t => {
          const transactionDate = new Date(t.date);
          if (t.type !== 'Recette') return false;
          
          if (interval === 'month') {
            // Filter by month
            return transactionDate.getMonth() === period && 
                  transactionDate.getFullYear() === periodDates[index].getFullYear();
          } else if (interval === 'week') {
            // Filter by week
            const weekStart = new Date(periodDates[index]);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            return transactionDate >= weekStart && transactionDate <= weekEnd;
          } else {
            // Filter by day
            return transactionDate.getDate() === period && 
                  transactionDate.getMonth() === periodDates[index].getMonth() &&
                  transactionDate.getFullYear() === periodDates[index].getFullYear();
          }
        })
        .reduce((sum, t) => sum + (t.montant || 0), 0);
    });
    
    setFilteredChartData({ data, labels });
  }, [transactions, revenueChartPeriod]);

  // We've replaced the menu with inline action buttons

  // Add a function to calculate transaction values in real-time with currency conversion
  const calculateTransactionValues = (data) => {
    const amount = Number(data.montant) || 0;
    const taxRate = data.enableTVA ? Number(data.customTVARate) : 0;
    const commissionRate = data.enableCommission && data.type === 'Recette' ? Number(data.customCommissionRate) : 0;
    
    // For foreign currency, convert to DZD for calculations
    let dzdAmount = amount;
    if (data.devise !== 'DZD' && data.tauxDeChange) {
      dzdAmount = amount * data.tauxDeChange;
    }
    
    // Calculate taxes and commissions on the DZD amount
    const taxAmount = dzdAmount * (taxRate / 100);
    const commissionAmount = dzdAmount * (commissionRate / 100);
    const totalAmount = dzdAmount + taxAmount;
    
    return {
      montantHT: amount,
      montantDZD: dzdAmount,
      taxAmount,
      commissionAmount,
      totalAmount,
      devise: data.devise
    };
  };

  // Add a memo to recalculate values when formData changes
  const calculatedValues = useMemo(() => {
    return calculateTransactionValues(formData);
  }, [formData]);

  // Always define these hooks regardless of loading state
  // Real-time financial calculations
  const financialSummary = React.useMemo(() => {
    // Default empty state
    if (!transactions || transactions.length === 0) {
      return {
        recettes: { total: 0, count: 0 },
        depenses: { total: 0, count: 0 },
        benefice: { net: 0, marge: 0 },
        enAttente: { total: 0, count: 0 }
      };
    }
    
    // Get total revenues from transactions
    const recettesTotal = transactions
      .filter(t => t.type === 'Recette')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
    
    // Get total expenses from transactions
    const depensesTotal = transactions
      .filter(t => t.type === 'Dépense')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
    
    // Calculate net profit
    const beneficeNet = recettesTotal - depensesTotal;
    
    // Calculate profit margin
    const margeBenefice = recettesTotal > 0 
      ? ((beneficeNet / recettesTotal) * 100).toFixed(1)
      : 0;
    
    // Get pending payments
    const enAttenteTotal = transactions
      .filter(t => t.statut === 'En attente')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
    
    return {
      recettes: {
        total: recettesTotal,
        count: transactions.filter(t => t.type === 'Recette').length
      },
      depenses: {
        total: depensesTotal,
        count: transactions.filter(t => t.type === 'Dépense').length
      },
      benefice: {
        net: beneficeNet,
        marge: margeBenefice
      },
      enAttente: {
        total: enAttenteTotal,
        count: transactions.filter(t => t.statut === 'En attente').length
      }
    };
  }, [transactions]);

  // Calculate trends based on previous data
  const calculateTrends = React.useMemo(() => {
    // Default empty state
    if (!transactions || transactions.length === 0) {
      return {
        recettes: '+0%',
        depenses: '+0%',
        benefice: '+0%',
        pending: '+0%'
      };
    }
    
    // Get current date
    const now = new Date();
    const currentMonth = now.getMonth();
    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const currentYear = now.getFullYear();
    const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    
    // Current month transactions
    const currentMonthTransactions = transactions.filter(t => {
      if (!t.date) return false;
      const date = new Date(t.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });
    
    // Previous month transactions
    const previousMonthTransactions = transactions.filter(t => {
      if (!t.date) return false;
      const date = new Date(t.date);
      return date.getMonth() === previousMonth && date.getFullYear() === previousYear;
    });
    
    // Calculate current month totals
    const currentRecettes = currentMonthTransactions
      .filter(t => t.type === 'Recette')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
      
    const currentDepenses = currentMonthTransactions
      .filter(t => t.type === 'Dépense')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
      
    const currentBenefice = currentRecettes - currentDepenses;
    
    // Calculate previous month totals
    const previousRecettes = previousMonthTransactions
      .filter(t => t.type === 'Recette')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
      
    const previousDepenses = previousMonthTransactions
      .filter(t => t.type === 'Dépense')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
      
    const previousBenefice = previousRecettes - previousDepenses;
    
    // Calculate trends
    const recettesTrend = previousRecettes > 0
      ? `${Math.round((currentRecettes / previousRecettes - 1) * 100)}%`
      : '+N/A';
      
    const depensesTrend = previousDepenses > 0
      ? `${Math.round((currentDepenses / previousDepenses - 1) * 100)}%`
      : '+N/A';
      
    const beneficeTrend = previousBenefice > 0
      ? `${Math.round((currentBenefice / previousBenefice - 1) * 100)}%`
      : '+N/A';
      
    // Calculate pending payments trend
    const currentPending = currentMonthTransactions
      .filter(t => t.statut === 'En attente')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
      
    const previousPending = previousMonthTransactions
      .filter(t => t.statut === 'En attente')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
      
    const pendingTrend = previousPending > 0
      ? `${Math.round((currentPending / previousPending - 1) * 100)}%`
      : '+N/A';
    
    return {
      recettes: recettesTrend,
      depenses: depensesTrend,
      benefice: beneficeTrend,
      pending: pendingTrend
    };
  }, [transactions]);

  // Enhanced statistics with interconnected data
  const statsCards = useMemo(() => [
    {
      title: 'Revenus Totaux',
      subtitle: `${financialSummary.recettes.count} transactions`,
      value: financialSummary.recettes.total,
      trend: '+12% ce mois',
      color: 'success',
      icon: TrendingUpIcon,
      isCurrency: true
    },
    {
      title: 'Dépenses Totales',
      subtitle: `${financialSummary.depenses.count} transactions`,
      value: financialSummary.depenses.total,
      trend: '-3% ce mois',
      color: 'error',
      icon: TrendingDownIcon,
      isCurrency: true
    },
    {
      title: 'Bénéfice Net',
      subtitle: `Marge: ${financialSummary.benefice.marge}%`,
      value: financialSummary.benefice.net,
      trend: `${financialSummary.benefice.marge > 20 ? 'Excellent' : 'Moyen'}`,
      color: financialSummary.benefice.marge > 20 ? 'success' : 'warning',
      icon: MoneyIcon,
      isCurrency: true
    },
    {
      title: 'Paiements en Attente',
      subtitle: `${financialSummary.enAttente.count} transactions`,
      value: financialSummary.enAttente.total,
      trend: 'Action requise',
      color: 'warning',
      icon: ScheduleIcon,
      isCurrency: true
    }
  ], [financialSummary]);

  useEffect(() => {
    // setTransactions(sampleTransactions); // This line is removed as transactions are now fetched from dataService
    // setFilteredTransactions(sampleTransactions); // This line is removed as filteredTransactions are now derived from transactions
  }, [settings.taxRate, settings.defaultCurrency]); // Update when settings change

  // Filter transactions based on search term and filters
  useEffect(() => {
    if (!transactionsLoading && transactions) {
      let filtered = [...transactions];
      
      // Apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(t => 
          t.reference?.toLowerCase().includes(search) || 
          t.description?.toLowerCase().includes(search) ||
          t.clientNom?.toLowerCase().includes(search) ||
          t.forfaitNom?.toLowerCase().includes(search)
        );
      }
      
      // Apply type filter
      if (filterType !== 'all') {
        filtered = filtered.filter(t => t.type === filterType);
      }
      
      // Apply status filter
      if (filterStatus !== 'all') {
        filtered = filtered.filter(t => t.statut === filterStatus);
      }
      
      // Apply period filter
      if (filterPeriod !== 'all') {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        
        switch (filterPeriod) {
          case 'month':
            filtered = filtered.filter(t => new Date(t.date) >= startOfMonth);
            break;
          case 'year':
            filtered = filtered.filter(t => new Date(t.date) >= startOfYear);
            break;
          default:
            break;
        }
      }
    
    setFilteredTransactions(filtered);
    }
  }, [transactions, searchTerm, filterType, filterStatus, filterPeriod, transactionsLoading]);

  // Broadcast current transactions for other pages (runs on every change)
  useEffect(() => {
    if (transactions && transactions.length) {
      window.dispatchEvent(new CustomEvent('financialDataUpdate', {
        detail: { source: 'finances', type: 'sync', data: transactions }
      }));
    }
  }, [transactions]);
  
  // Connect transactions with reservations, clients, and packages
  useEffect(() => {
    if (!transactionsLoading && !clientsLoading && !packagesLoading && !reservationsLoading) {
      // Create enhanced transactions with interconnected data
      const enhancedTransactions = transactions.map(transaction => {
        // Find associated reservation
        const reservation = transaction.reservationId ? 
          reservations.find(r => r.id === transaction.reservationId) : null;
          
        // Find associated client
        const client = transaction.clientId ? 
          clients.find(c => c.id === transaction.clientId) : null;
          
        // Find associated package
        const forfait = reservation?.forfaitId ? 
          packages.find(p => p.id === reservation.forfaitId) : null;
          
        // Find associated supplier
        const supplier = transaction.fournisseurId ? 
          suppliers.find(s => s.id === transaction.fournisseurId) : null;
          
        // Return enhanced transaction with all related data
        return {
          ...transaction,
          clientNom: client?.nom || transaction.client || 'Client inconnu',
          forfaitNom: forfait?.titre || reservation?.forfait || 'Forfait inconnu',
          fournisseurNom: supplier?.nom || transaction.fournisseur || 'Fournisseur inconnu',
          reservationDetails: reservation ? {
            dateDepart: reservation.dateDepart,
            dateRetour: reservation.dateRetour,
            destination: reservation.destination,
            nombrePassagers: reservation.nombrePassagers
          } : null
        };
      });
      
      // Update filtered transactions
      setFilteredTransactions(enhancedTransactions);
      
      // Set up real-time data synchronization
      const handleStorageChange = () => {
        console.log('Storage changed, updating financial data...');
        // This will trigger the useEffect that depends on transactions, etc.
      };
      
      // Listen for changes in other components
      window.addEventListener('storage', handleStorageChange);
      
      // Clean up
      return () => {
        window.removeEventListener('storage', handleStorageChange);
      };
    }
  }, [transactions, clients, packages, reservations, transactionsLoading, clientsLoading, packagesLoading, reservationsLoading, suppliers, suppliersLoading]);
  
  // Add a function to trigger real-time updates from other pages
  const refreshFinancialData = useCallback(() => {
    console.log('Manually refreshing financial data...');
    
    // Force reload of data from localStorage
    const localTransactions = localStorage.getItem('transactionsData');
    if (localTransactions) {
      try {
        const parsedTransactions = JSON.parse(localTransactions);
        // Update transactions state directly for immediate UI update
        setFilteredTransactions(parsedTransactions);
      } catch (error) {
        console.error('Error parsing transactions from localStorage:', error);
      }
    }
    
    // Dispatch a storage event to trigger updates in other components
    window.dispatchEvent(new Event('storage'));
    
    // Show a brief notification
    setSnackbar({
      open: true,
      message: 'Données financières synchronisées',
      severity: 'info'
    });
    
    // Auto-close snackbar after 2 seconds
    setTimeout(() => {
      setSnackbar(prev => ({...prev, open: false}));
    }, 2000);
  }, []);

  // Listen for updates from other pages
  useEffect(() => {
    const handleExternalUpdate = (event) => {
      if (event.detail && event.detail.source) {
        // Show notification
        setUpdateNotification({
          show: true,
          source: event.detail.source,
          message: event.detail.message || 'Données mises à jour'
        });
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
          setUpdateNotification({
            show: false,
            source: '',
            message: ''
          });
        }, 5000);
        
        // Refresh data
        refreshFinancialData();
        
        // Log detailed update information
        console.log('Finance page received update:', event.detail);
        
        // Update specific data based on update type
        if (event.detail.type === 'reservation_added' || event.detail.type === 'reservation_updated') {
          // If a reservation was added or updated, we might need to update financial data
          // This will trigger our useEffect that depends on reservations
          window.dispatchEvent(new Event('storage'));
        }
        
        if (event.detail.type === 'client_added' || event.detail.type === 'client_updated') {
          // If a client was added or updated, refresh client data
          window.dispatchEvent(new Event('storage'));
        }
        
        if (event.detail.type === 'package_added' || event.detail.type === 'package_updated') {
          // If a package was added or updated, refresh package data
          window.dispatchEvent(new Event('storage'));
        }
      }
    };
    
    // Listen for custom events from other pages
    window.addEventListener('financialDataUpdate', handleExternalUpdate);
    window.addEventListener('reservationDataUpdate', handleExternalUpdate);
    window.addEventListener('clientDataUpdate', handleExternalUpdate);
    window.addEventListener('packageDataUpdate', handleExternalUpdate);
    
    return () => {
      window.removeEventListener('financialDataUpdate', handleExternalUpdate);
      window.removeEventListener('reservationDataUpdate', handleExternalUpdate);
      window.removeEventListener('clientDataUpdate', handleExternalUpdate);
      window.removeEventListener('packageDataUpdate', handleExternalUpdate);
    };
  }, [refreshFinancialData]);

  // Add a more robust storage event handler to ensure real-time updates
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Check which data was updated
      if (e.key === 'transactionsData' || e.key === 'reservationsData' || 
          e.key === 'clientsData' || e.key === 'packagesData' || e.key === null) {
        console.log(`Storage changed (${e.key}), refreshing financial data...`);
        
        // Refresh financial data
        refreshFinancialData();
        
        // Show notification
        setUpdateNotification({
          show: true,
          source: 'storage',
          message: 'Données financières mises à jour'
        });
        
        // Auto-hide after 3 seconds
        setTimeout(() => {
          setUpdateNotification({
            show: false,
            source: '',
            message: ''
          });
        }, 3000);
      }
    };
    
    // Listen for storage events
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [refreshFinancialData]);

  // Render loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          Gestion Financière
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Chargement des données financières...
        </Typography>
        
        <LinearProgress sx={{ mb: 3 }} />
        
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        
        <Box sx={{ mt: 3 }}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      </Box>
    );
  }
  
  // Render error state
  if (transactionsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Erreur lors du chargement des données financières: {transactionsError}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
        >
          Réessayer
        </Button>
      </Box>
    );
  }

  // Chart data
  const revenueChartData = [150000, 180000, 210000, 190000, 220000, 240000];
  const pieChartData = { culturel: 40, religieux: 25, luxe: 20, autres: 15 };

  // Enhanced client details function with more data
  const getClientDetails = (clientId) => {
    const client = clients.find(c => Number(c.id) === Number(clientId));
    if (!client) return null;
    
    // Get all transactions for this client
    const clientTransactions = transactions.filter(t => Number(t.clientId) === Number(clientId));
    const totalSpent = clientTransactions
      .filter(t => t.type === 'Recette')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
      
    // Get client reservations
    const clientReservations = reservations.filter(r => Number(r.clientId) === Number(clientId));
    
    return {
      ...client,
      totalSpent,
      reservationCount: clientReservations.length,
      lastTransaction: clientTransactions.length > 0 ? 
        clientTransactions.sort((a, b) => new Date(b.date) - new Date(a.date))[0] : null
    };
  };
  
  // Enhanced package details function with more data
  const getPackageDetails = (forfaitId) => {
    const packageInfo = packages.find(p => Number(p.id) === Number(forfaitId));
    if (!packageInfo) return null;
    
    // Get all reservations for this package
    const packageReservations = reservations.filter(r => Number(r.forfaitId) === Number(forfaitId));
    const totalRevenue = packageReservations.reduce((sum, r) => sum + (r.prixTotal || 0), 0);
    
    return {
      ...packageInfo,
      reservationCount: packageReservations.length,
      totalRevenue
    };
  };
  
  // Enhanced reservation details function with more data
  const getReservationDetails = (reservationId) => {
    const reservation = reservations.find(r => r.id === reservationId);
    if (!reservation) return null;
    
    // Get all transactions for this reservation
    const reservationTransactions = transactions.filter(t => t.reservationId === reservationId);
    const totalPaid = reservationTransactions
      .filter(t => t.type === 'Recette')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
      
    const remainingAmount = reservation.prixTotal - totalPaid;
    
    return {
      ...reservation,
      totalPaid,
      remainingAmount,
      paymentStatus: totalPaid >= reservation.prixTotal ? 'Payé' : 'Partiel'
    };
  };

  // Add client selection with reservation data
  const renderClientDropdownItem = (client) => {
    // Find all reservations for this client - ensure IDs are converted to numbers for comparison
    const clientReservations = reservations.filter(r => Number(r.clientId) === Number(client.id));
    
    // Calculate total spent by client
    const totalSpent = clientReservations.reduce((sum, r) => sum + (r.prixTotal || 0), 0);
    
    return (
      <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ width: 32, height: 32 }}>
          {client.prenom?.[0] || client.nom?.[0] || 'C'}
        </Avatar>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {client.nom}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatCurrency(totalSpent)} • {clientReservations.length} réservation{clientReservations.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Update handleOpenDialog to include tax, commission settings, and currency support
  const handleOpenDialog = (transaction = null) => {
    if (transaction) {
      // Calculate if TVA was applied in this transaction
      const hasTVA = transaction.taxes > 0;
      const tvaRate = hasTVA && transaction.montantHT > 0 
        ? Math.round((transaction.taxes / transaction.montantHT) * 100) 
        : settings.taxRate;
      
      // Calculate if commission was applied in this transaction
      const hasCommission = transaction.commission > 0;
      const commissionRate = hasCommission && transaction.montantHT > 0
        ? Math.round((transaction.commission / transaction.montantHT) * 100)
        : 10;
      
      // Handle foreign currency transactions
      const devise = transaction.devise || 'DZD';
      const showConversion = devise !== 'DZD';
      
      // Get the exchange rate, either from the transaction or from settings
      let tauxDeChange = transaction.tauxDeChange;
      if (showConversion && (!tauxDeChange || tauxDeChange === 0) && settings.exchangeRates && settings.exchangeRates[devise]) {
        tauxDeChange = settings.exchangeRates[devise].rate;
      }
      
      const montantDZD = transaction.montantDZD || (showConversion ? transaction.montant * (tauxDeChange || 1) : transaction.montant);
      
      setEditingTransaction(transaction);
      setFormData({
        type: transaction.type,
        categorie: transaction.categorie,
        clientId: transaction.clientId || '',
        client: transaction.client,
        description: transaction.description,
        montant: transaction.montant,
        devise: devise,
        showConversion: showConversion,
        montantDZD: montantDZD,
        tauxDeChange: tauxDeChange,
        methode: transaction.methode,
        statut: transaction.statut,
        notes: transaction.notes,
        enableTVA: hasTVA,
        customTVARate: tvaRate,
        enableCommission: hasCommission,
        customCommissionRate: commissionRate,
        reservationId: transaction.reservationId || '',
        date: transaction.date || new Date().toISOString().split('T')[0]
      });
    } else {
      setEditingTransaction(null);
      setFormData({
        type: 'Recette',
        categorie: 'Réservation',
        clientId: '',
        client: '',
        description: '',
        montant: 0,
        devise: 'DZD',
        showConversion: false,
        montantDZD: 0,
        tauxDeChange: null,
        methode: 'Virement bancaire',
        statut: 'En attente',
        notes: '',
        enableTVA: true,
        customTVARate: settings.taxRate,
        enableCommission: true,
        customCommissionRate: 10,
        reservationId: '',
        date: new Date().toISOString().split('T')[0]
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTransaction(null);
  };

  // Update handleSaveTransaction to use custom tax and commission rates and handle currency conversion
  const handleSaveTransaction = () => {
    const selectedClient = clients.find(c => c.id === formData.clientId);
    
    // Calculate tax and commission based on custom rates and enable/disable settings
    const taxRate = formData.enableTVA ? formData.customTVARate : 0;
    const commissionRate = formData.enableCommission && formData.type === 'Recette' ? formData.customCommissionRate : 0;
    
    // Handle currency conversion
    let montantDZD = formData.montant;
    let tauxDeChange = null;
    let dateConversion = null;
    
    // If foreign currency is used, calculate DZD equivalent
    if (formData.devise !== 'DZD') {
      // Always use the exchange rate from settings
      if (settings.exchangeRates && settings.exchangeRates[formData.devise]) {
        tauxDeChange = settings.exchangeRates[formData.devise].rate;
      }
      
      // Calculate the DZD amount
      montantDZD = formData.montant * tauxDeChange;
      dateConversion = new Date().toISOString();
    }
    
    // Use the appropriate amount for tax and commission calculations
    const baseAmount = formData.devise === 'DZD' ? formData.montant : montantDZD;
    
    const transactionData = {
      ...formData,
      montantHT: formData.montant,
      montantDZD: formData.devise === 'DZD' ? formData.montant : montantDZD,
      tauxDeChange: formData.devise === 'DZD' ? null : tauxDeChange,
      dateConversion: formData.devise === 'DZD' ? null : dateConversion,
      montantTTC: formData.enableTVA ? baseAmount * (1 + taxRate / 100) : baseAmount,
      taxes: formData.enableTVA ? baseAmount * (taxRate / 100) : 0,
      commission: formData.enableCommission && formData.type === 'Recette' ? baseAmount * (commissionRate / 100) : 0,
      client: selectedClient ? selectedClient.nom : formData.client,
      date: formData.date || new Date().toISOString().split('T')[0],
      reference: `REF-${new Date().getFullYear()}-${String(transactions.length + 1).padStart(3, '0')}`
    };

    // Get current data from localStorage
    const currentTransactions = JSON.parse(localStorage.getItem('transactionsData')) || transactions || [];

    if (editingTransaction) {
      // Update existing transaction
      const updatedTransactions = currentTransactions.map(t => 
        t.id === editingTransaction.id ? { ...t, ...transactionData } : t
      );
      
      // Sort by date (newest first)
      const sortedTransactions = updatedTransactions
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      
      // Save to localStorage - don't limit to just 5 transactions
      localStorage.setItem('transactionsData', JSON.stringify(sortedTransactions));
      
      setSnackbar({
        open: true,
        message: 'Transaction modifiée avec succès - Données synchronisées',
        severity: 'success'
      });
      
      // Notify other pages
      notifyOtherPages('transaction_updated', transactionData);
    } else {
      // Add new transaction
      const newTransaction = {
        ...transactionData,
        id: `TXN${String(Math.max(...currentTransactions.map(t => parseInt(t.id.slice(3) || '0')), 0) + 1).padStart(3, '0')}`
      };
      
      // Add new transaction, sort by date (newest first)
      const updatedTransactions = [...currentTransactions, newTransaction]
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      
      // Save to localStorage - don't limit to just 5 transactions
      localStorage.setItem('transactionsData', JSON.stringify(updatedTransactions));
      
      setSnackbar({
        open: true,
        message: 'Transaction ajoutée avec succès - Impact financier calculé',
        severity: 'success'
      });
      
      // If this is a payment for a reservation, update the reservation payment status
      if (formData.reservationId) {
        const currentReservations = JSON.parse(localStorage.getItem('reservationsData')) || reservations || [];
        const reservation = currentReservations.find(r => r.id === formData.reservationId);
        
        if (reservation) {
          // Calculate total paid amount including this new transaction
          const totalPaid = transactions
            .filter(t => t.reservationId === formData.reservationId && t.type === 'Recette')
            .reduce((sum, t) => sum + (t.montant || 0), 0) + formData.montant;
          
          // Update reservation payment status
          const updatedReservation = {
            ...reservation,
            statutPaiement: totalPaid >= reservation.prixTotal ? 'Soldé' : 'Acompte versé',
            acompte: totalPaid
          };
          
          // Save updated reservation
          const updatedReservations = currentReservations.map(r => 
            r.id === formData.reservationId ? updatedReservation : r
          );
          
          localStorage.setItem('reservationsData', JSON.stringify(updatedReservations));
          
          // Notify other pages about reservation update
          notifyOtherPages('reservation_updated', updatedReservation);
        }
      }
      
      // Notify other pages
      notifyOtherPages('transaction_added', newTransaction);
    }
    
    // Trigger refresh for all components
    window.dispatchEvent(new Event('storage'));
    
    // Force dashboard to update immediately
    window.dispatchEvent(new CustomEvent('dashboardUpdate', {
      detail: { type: 'finance_change', source: 'finances' }
    }));
    
    handleCloseDialog();
  };

  const handleDeleteTransaction = (transactionId) => {
    // Get current data from localStorage
    const currentTransactions = JSON.parse(localStorage.getItem('transactionsData')) || transactions || [];
    
    // Find the transaction to delete
    const transactionToDelete = currentTransactions.find(t => t.id === transactionId);
    
    // Filter out the deleted transaction
    const updatedTransactions = currentTransactions.filter(t => t.id !== transactionId);
    
    // Sort by date (newest first)
    const sortedTransactions = updatedTransactions
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // Save to localStorage - don't limit to just 5 transactions
    localStorage.setItem('transactionsData', JSON.stringify(sortedTransactions));
    
    setSnackbar({
      open: true,
      message: 'Transaction supprimée - Finances mises à jour',
      severity: 'warning'
    });
    
    // Notify other pages
    notifyOtherPages('transaction_deleted', transactionToDelete);
    
    // Trigger refresh for all components
    window.dispatchEvent(new Event('storage'));
    
    // Force dashboard to update immediately
    window.dispatchEvent(new CustomEvent('dashboardUpdate', {
      detail: { type: 'finance_change', source: 'finances' }
    }));
  };

  const getTypeColor = (type) => {
    return type === 'Recette' ? 'success' : 'error';
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'Terminé': return 'success';
      case 'En attente': return 'warning';
      case 'Annulé': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (statut) => {
    return statut === 'Confirmé' ? <CheckCircleIcon color="success" /> : <HourglassBottomIcon color="warning" />;
  };

  const getStatusIconOriginal = (statut) => {
    switch (statut) {
      case 'Terminé': return <CheckCircleIcon />;
      case 'En attente': return <ScheduleIcon />;
      case 'Annulé': return <CancelIcon />;
      default: return <WarningIcon />;
    }
  };

  const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} id={`finance-tabpanel-${index}`}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );

  // Function to format currency correctly
  const formatCurrencyForPDF = (amount, currency = 'DZD') => {
    // Convert number to string with spaces as thousand separators
    const numberPart = Math.round(amount || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${numberPart} ${currency}`;
  };

  const exportFinancesToPDF = () => {
    try {
      // Create new PDF document
      const doc = new jsPDF();
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const successColor = [16, 185, 129]; // green
      const errorColor = [239, 68, 68]; // red
      const textColor = [31, 41, 55]; // dark gray
      
      // Add header
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Add agency logo and name
      if (settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
      }
      
      // Add title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text("Rapport Financier", pageWidth - margin, 17, { align: 'right' });
      
      // Add date
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(10);
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, margin, 35);
      
      // Add period filter
      const period = filterPeriod === 'all' ? 'Toutes les périodes' : `Période: ${filterPeriod}`;
      doc.text(`Filtres: ${period} | Type: ${filterType === 'all' ? 'Tous les types' : filterType}`, margin, 42);
      
      // Add summary section
      doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, 48, pageWidth - margin, 48);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Résumé Financier", margin, 58);
      
      // KPI summary
      const revenus = financialSummary.recettes.total || 0;
      const depenses = financialSummary.depenses.total || 0;
      const benefice = revenus - depenses;
      const margePercent = revenus > 0 ? ((benefice / revenus) * 100).toFixed(1) : 0;
      
      const kpiData = [
        { label: "Revenus Totaux", value: formatCurrencyForPDF(revenus) },
        { label: "Dépenses Totales", value: formatCurrencyForPDF(depenses) },
        { label: "Bénéfice Net", value: formatCurrencyForPDF(benefice) },
        { label: "Marge", value: `${margePercent}%` },
        { label: "Transactions", value: `${filteredTransactions.length} (${transactions?.filter(t => t.type === 'Recette').length || 0} recettes, ${transactions?.filter(t => t.type === 'Dépense').length || 0} dépenses)` }
      ];
      
      // Add KPI table
      doc.autoTable({
        startY: 62,
        head: [['Indicateur', 'Valeur']],
        body: kpiData.map(item => [item.label, item.value]),
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add status breakdown
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Répartition par Statut", margin, doc.lastAutoTable.finalY + 15);
      
      // Calculate status stats
      const statusCounts = {};
      filteredTransactions.forEach(transaction => {
        if (!statusCounts[transaction.statut]) {
          statusCounts[transaction.statut] = {
            count: 0,
            amount: 0
          };
        }
        statusCounts[transaction.statut].count++;
        // Use montantDZD for foreign currency transactions
        const amount = transaction.devise !== 'DZD' ? (transaction.montantDZD || 0) : (transaction.montant || 0);
        statusCounts[transaction.statut].amount += amount;
      });
      
      const statusStats = Object.entries(statusCounts)
        .map(([status, data]) => ({
          status,
          count: data.count,
          amount: data.amount,
          percentage: (data.count / filteredTransactions.length * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count);
      
      // Add status table
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Statut', 'Nombre', 'Montant Total', 'Pourcentage']],
        body: statusStats.map(item => [
          item.status,
          item.count.toString(),
          formatCurrencyForPDF(item.amount),
          `${item.percentage}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: secondaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add transactions by type
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Répartition par Type", margin, doc.lastAutoTable.finalY + 15);
      
      // Calculate type breakdown
      const typeData = [
        { 
          type: "Recettes",
          count: filteredTransactions.filter(t => t.type === 'Recette').length,
          amount: filteredTransactions
            .filter(t => t.type === 'Recette')
            .reduce((sum, t) => {
              const amount = t.devise !== 'DZD' ? (t.montantDZD || 0) : (t.montant || 0);
              return sum + amount;
            }, 0)
        },
        { 
          type: "Dépenses",
          count: filteredTransactions.filter(t => t.type === 'Dépense').length,
          amount: filteredTransactions
            .filter(t => t.type === 'Dépense')
            .reduce((sum, t) => {
              const amount = t.devise !== 'DZD' ? (t.montantDZD || 0) : (t.montant || 0);
              return sum + amount;
            }, 0)
        }
      ];
      
      // Add type table
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Type', 'Nombre', 'Montant Total', 'Pourcentage']],
        body: typeData.map(item => [
          item.type,
          item.count.toString(),
          formatCurrencyForPDF(item.amount),
          `${(item.count / filteredTransactions.length * 100).toFixed(1)}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: successColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add transactions list
      doc.addPage();
      
      // Add header
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, 10, 30, 10, 1, 1, 'F');
      doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("TravelIT", margin + 6, 17);
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text("Liste des Transactions", pageWidth - margin, 17, { align: 'right' });
      
      // Add transactions table
      const transactionsTableData = filteredTransactions.map(transaction => {
        // Get client or supplier name if available
        let relatedParty = 'N/A';
        if (transaction.clientId) {
          const client = getClientDetails(transaction.clientId);
          relatedParty = client ? client.nom : 'Client inconnu';
        } else if (transaction.fournisseurId) {
          const supplier = suppliers.find(s => s.id === transaction.fournisseurId);
          relatedParty = supplier ? supplier.nom : 'Fournisseur inconnu';
        }
        
        return [
          transaction.reference,
          new Date(transaction.date).toLocaleDateString('fr-FR'),
          transaction.description || 'N/A',
          relatedParty,
          transaction.type,
          formatCurrencyForPDF(transaction.devise !== 'DZD' ? transaction.montantDZD : transaction.montant) + 
          (transaction.devise !== 'DZD' ? ` (${formatCurrencyForPDF(transaction.montant, transaction.devise)})` : ''),
          transaction.statut
        ];
      });
      
      doc.autoTable({
        startY: 40,
        head: [['Référence', 'Date', 'Description', 'Client/Fournisseur', 'Type', 'Montant', 'Statut']],
        body: transactionsTableData,
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 20 },
          2: { cellWidth: 40 },
          3: { cellWidth: 35 },
          4: { cellWidth: 20 },
          5: { cellWidth: 25 },
          6: { cellWidth: 20 }
        },
        margin: { left: margin, right: margin },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: function (data) {
          // Add header on each new page
          doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.rect(0, 0, pageWidth, 25, 'F');
          
          // Add agency logo and name
          if (settings.agencyLogo) {
            try {
              // Add the agency logo from base64 string
              doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
              
              // Add agency name
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
            } catch (logoError) {
              console.error('Error adding logo to PDF:', logoError);
              // Fallback to text only
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
            }
          } else {
            // No logo, just add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.text("Liste des Transactions", pageWidth - margin, 17, { align: 'right' });
          
          // Add footer
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Page ${pageNumber}`, margin, pageHeight - 10);
          doc.text(`${settings.agencyName || 'Agence de Voyage'} | Page ${pageNumber} sur ${doc.internal.getNumberOfPages()}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        }
      });
      
      // Save the PDF with agency name
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `${settings.agencyName || 'Agence'}_Rapport_Financier_${dateStr}.pdf`;
      doc.save(fileName);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Export PDF réussi',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'export PDF',
        severity: 'error'
      });
    }
  };

  // Function to dispatch updates to other pages
  const notifyOtherPages = (updateType, data) => {
    // Create a custom event to notify other pages
    const event = new CustomEvent('financialDataUpdate', {
      detail: {
        source: 'finances',
        type: updateType,
        data: data,
        message: `Mise à jour financière: ${updateType}`
      }
    });
    
    // Dispatch the event
    window.dispatchEvent(event);
  };

  // Add a function to get client data with reservations
  const getEnhancedClientData = (clientId) => {
    if (!clientId || !clients || !reservations) return null;
    
    const client = clients.find(c => Number(c.id) === Number(clientId));
    if (!client) return null;
    
    // Get client reservations
    const clientReservations = reservations.filter(r => Number(r.clientId) === Number(clientId));
    
    // Calculate total spent
    const totalSpent = clientReservations.reduce((sum, r) => sum + (r.prixTotal || 0), 0);
    
    return {
      ...client,
      reservations: clientReservations,
      reservationCount: clientReservations.length,
      totalSpent
    };
  };

  // Add a function to populate transaction data based on client selection
  const handleClientSelection = (event, newValue) => {
    if (!newValue) {
      setFormData({
        ...formData,
        clientId: '',
        client: ''
      });
      return;
    }
    
    // Get client data
    const clientId = newValue.id;
    const clientData = getEnhancedClientData(clientId);
    
    // Update form with client data
    setFormData({
      ...formData,
      clientId,
      client: newValue.nom,
      // If it's a new transaction for a reservation, suggest the latest reservation
      ...(formData.categorie === 'Réservation' && clientData?.reservations?.length > 0 && !editingTransaction ? {
        description: `Paiement pour ${clientData.reservations[0].forfait || 'réservation'}`,
        montant: clientData.reservations[0].prixTotal || 0
      } : {})
    });
  };

  // Add a component to show client reservations in a dropdown
  const ClientReservationsDropdown = ({ clientId, onSelectReservation }) => {
    // Get client reservations - ensure IDs are converted to numbers for comparison
    const clientReservations = reservations.filter(r => Number(r.clientId) === Number(clientId));
    
    if (!clientReservations.length) {
      return (
        <Typography variant="caption" color="text.secondary">
          Aucune réservation trouvée pour ce client
        </Typography>
      );
    }
    
    return (
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
          Réservations du client:
        </Typography>
        <Paper variant="outlined" sx={{ maxHeight: 150, overflow: 'auto' }}>
          <List dense>
            {clientReservations.map((reservation) => (
              <ListItem 
                key={reservation.id} 
                button 
                onClick={() => onSelectReservation(reservation)}
                sx={{ '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' } }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <FlightIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={reservation.forfait || reservation.destination}
                  secondary={`${formatDate(reservation.dateDepart)} • ${formatCurrency(reservation.prixTotal)}`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>
    );
  };

  // Add a function to handle reservation selection
  const handleReservationSelection = (reservation) => {
    // Calculate remaining amount to pay
    const reservationTransactions = transactions
      .filter(t => t.reservationId === reservation.id && t.type === 'Recette')
      .reduce((sum, t) => sum + (t.montant || 0), 0);
    
    const remainingAmount = Math.max(0, reservation.prixTotal - reservationTransactions);
    
    // Update form data with reservation details
    setFormData({
      ...formData,
      reservationId: reservation.id,
      description: `Paiement pour ${reservation.forfait || 'réservation'} (${formatDate(reservation.dateDepart)})`,
      montant: remainingAmount,
      notes: `Réservation ID: ${reservation.id} - ${remainingAmount < reservation.prixTotal ? 'Paiement partiel' : 'Paiement intégral'}`
    });
  };

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  // Add a function to handle viewing transaction details
  const handleViewDetails = (transaction) => {
    if (!transaction) return;
    
    // Create a formatted details object
    const details = {
      id: transaction.id,
      reference: transaction.reference || '-',
      date: formatDate(transaction.date),
      type: transaction.type,
      categorie: transaction.categorie,
      description: transaction.description || '-',
      montantHT: formatCurrency(transaction.montantHT || transaction.montant),
      taxes: formatCurrency(transaction.taxes || 0),
      montantTTC: formatCurrency(transaction.montantTTC || transaction.montant),
      commission: transaction.commission ? formatCurrency(transaction.commission) : '-',
      methode: transaction.methode || '-',
      statut: transaction.statut,
      notes: transaction.notes || '-'
    };
    
    // Get related entities
    if (transaction.clientId) {
      const client = clients.find(c => Number(c.id) === Number(transaction.clientId));
      if (client) {
        details.client = `${client.nom} ${client.prenom} (${client.email})`;
      }
    }
    
    if (transaction.reservationId) {
      const reservation = reservations.find(r => r.id === transaction.reservationId);
      if (reservation) {
        details.reservation = `${reservation.forfait} (${formatDate(reservation.dateDepart)})`;
      }
    }
    
    if (transaction.fournisseurId) {
      const fournisseur = suppliers.find(s => s.id === transaction.fournisseurId);
      if (fournisseur) {
        details.fournisseur = `${fournisseur.nom} (${fournisseur.type})`;
      }
    }
    
    // Show details in a dialog
    setDetailsData(details);
    setOpenDetailsDialog(true);
  };
  
  // Add a function to handle printing receipt
  const handlePrintReceipt = (transaction) => {
    if (!transaction) return;
    
    try {
      // Create a new PDF document
      const doc = new jsPDF();
      
      // Add header
      doc.setFillColor(30, 64, 175);
      doc.rect(0, 0, 210, 25, 'F');
      
      // Add title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text("REÇU DE PAIEMENT", 105, 15, { align: 'center' });
      
      // Add logo placeholder
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(10, 10, 30, 10, 1, 1, 'F');
      doc.setTextColor(30, 64, 175);
      doc.setFontSize(12);
      doc.text("TravelIT", 16, 17);
      
      // Add transaction details
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Détails de la transaction", 20, 40);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      
      // Transaction info
      doc.text(`Référence: ${transaction.reference || transaction.id}`, 20, 50);
      doc.text(`Date: ${formatDate(transaction.date)}`, 20, 57);
      doc.text(`Type: ${transaction.type}`, 20, 64);
      doc.text(`Catégorie: ${transaction.categorie}`, 20, 71);
      doc.text(`Méthode: ${transaction.methode || '-'}`, 20, 78);
      doc.text(`Statut: ${transaction.statut}`, 20, 85);
      
      // Client/Supplier info
      doc.setFont('helvetica', 'bold');
      doc.text("Informations client/fournisseur", 20, 100);
      doc.setFont('helvetica', 'normal');
      
      if (transaction.clientId) {
        const client = clients.find(c => c.id === transaction.clientId);
        if (client) {
          doc.text(`Client: ${client.nom} ${client.prenom}`, 20, 110);
          doc.text(`Email: ${client.email || '-'}`, 20, 117);
          doc.text(`Téléphone: ${client.telephone || '-'}`, 20, 124);
        }
      } else if (transaction.fournisseurId) {
        const fournisseur = suppliers.find(s => s.id === transaction.fournisseurId);
        if (fournisseur) {
          doc.text(`Fournisseur: ${fournisseur.nom}`, 20, 110);
          doc.text(`Type: ${fournisseur.type || '-'}`, 20, 117);
          doc.text(`Contact: ${fournisseur.contact || '-'}`, 20, 124);
        }
      }
      
      // Amount details
      doc.setFont('helvetica', 'bold');
      doc.text("Détails du montant", 20, 140);
      doc.setFont('helvetica', 'normal');
      
      doc.text(`Montant HT: ${formatCurrency(transaction.montantHT || transaction.montant)}`, 20, 150);
      doc.text(`Taxes (${settings.taxRate}%): ${formatCurrency(transaction.taxes || 0)}`, 20, 157);
      doc.text(`Montant TTC: ${formatCurrency(transaction.montantTTC || transaction.montant)}`, 20, 164);
      
      if (transaction.commission) {
        doc.text(`Commission: ${formatCurrency(transaction.commission)}`, 20, 171);
      }
      
      // Notes
      if (transaction.notes) {
        doc.setFont('helvetica', 'bold');
        doc.text("Notes", 20, 185);
        doc.setFont('helvetica', 'normal');
        doc.text(transaction.notes, 20, 192);
      }
      
      // Footer
      doc.setFontSize(8);
      doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 105, 280, { align: 'center' });
      
      // Save the PDF
      doc.save(`recu-${transaction.id}.pdf`);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Reçu généré avec succès',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Erreur lors de la génération du reçu:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la génération du reçu',
        severity: 'error'
      });
    }
  };

  // Removed menu handlers as we're using inline action buttons

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            mb: 1
          }}
        >
          Gestion Financière
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Gérez vos finances, suivez les paiements et analysez la rentabilité
        </Typography>
      </Box>

      {/* Real-time update notification */}
      <Snackbar
        open={updateNotification.show}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 7 }}
      >
        <Alert 
          severity="info" 
          icon={
            <Box sx={{ animation: 'spin 2s linear infinite', display: 'flex', alignItems: 'center' }}>
              <SyncIcon />
            </Box>
          }
          sx={{ 
            width: '100%', 
            '& .MuiAlert-icon': { mr: 1 },
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Mise à jour en temps réel
            </Typography>
            <Typography variant="caption">
              {updateNotification.message}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Regular notification snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isLoading ? (
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
            Chargement des données financières interconnectées...
          </Typography>
        </Box>
      ) : (
        <>
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {stat.isCurrency ? formatCurrency(stat.value) : stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {stat.subtitle}
                    </Typography>
                    <Chip 
                      label={stat.trend} 
                      size="small" 
                      color={stat.color}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Avatar 
                    sx={{ 
                      bgcolor: `${stat.color}.main`,
                      width: 56,
                      height: 56
                    }}
                  >
                    <stat.icon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

          {/* Charts Row */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Évolution des Revenus
                  </Typography>
                    <FormControl size="small" sx={{ width: 150 }}>
                      <Select
                        value={revenueChartPeriod}
                        onChange={(e) => setRevenueChartPeriod(e.target.value)}
                        displayEmpty
                        variant="outlined"
                        sx={{ borderRadius: 2, fontSize: '0.875rem' }}
                      >
                        <MenuItem value="year">Dernière année</MenuItem>
                        <MenuItem value="6months">6 derniers mois</MenuItem>
                        <MenuItem value="month">Dernier mois</MenuItem>
                        <MenuItem value="week">Dernière semaine</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <SimpleLineChart data={filteredChartData} color="#1976d2" />
                </CardContent>
              </Card>
            </Grid>
      </Grid>

      {/* Tabs Navigation */}
      <Card 
        sx={{ 
          mb: 3,
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ px: 2 }}
        >
          <Tab label="Transactions" icon={<ReceiptIcon />} />
        </Tabs>
      </Card>

      {/* Tab Content */}
      <TabPanel value={activeTab} index={0}>
        {/* Search and Filters */}
        <Card 
          sx={{ 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder="Rechercher une transaction..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filterType}
                    label="Type"
                    onChange={(e) => setFilterType(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="all">Tous</MenuItem>
                    <MenuItem value="Recette">Recettes</MenuItem>
                    <MenuItem value="Dépense">Dépenses</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Statut"
                    onChange={(e) => setFilterStatus(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="all">Tous</MenuItem>
                    <MenuItem value="Terminé">Terminés</MenuItem>
                    <MenuItem value="En attente">En attente</MenuItem>
                    <MenuItem value="Annulé">Annulés</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Période</InputLabel>
                  <Select
                    value={filterPeriod}
                    label="Période"
                    onChange={(e) => setFilterPeriod(e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="all">Toutes</MenuItem>
                        <MenuItem value="month">Ce mois</MenuItem>
                        <MenuItem value="year">Cette année</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ExportIcon />}
                    sx={{ borderRadius: 2, flex: 1 }}
                    onClick={exportFinancesToPDF}
                  >
                    Exporter
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ 
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
                      flex: 1
                    }}
                  >
                    Nouvelle
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

            {/* Transaction Table with Real-Time Data */}
        <Card 
          sx={{ 
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                      <TableCell>Référence</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Client/Fournisseur</TableCell>
                      <TableCell>Montant</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                    {filteredTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Box sx={{ py: 3 }}>
                            <Typography variant="body1">
                              Aucune transaction trouvée
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Essayez de modifier vos filtres ou créez une nouvelle transaction
                            </Typography>
                            <Button 
                              variant="contained" 
                              sx={{ mt: 2 }}
                              onClick={() => handleOpenDialog()}
                            >
                              Nouvelle Transaction
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTransactions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((transaction) => {
                          // Get interconnected data
                          const client = transaction.clientId ? getClientDetails(transaction.clientId) : null;
                          const forfait = transaction.forfaitId ? getPackageDetails(transaction.forfaitId) : null;
                          const reservation = transaction.reservationId ? getReservationDetails(transaction.reservationId) : null;
                          
                          return (
                            <TableRow key={transaction.id}>
                      <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                                    sx={{ 
                                      width: 32, 
                                      height: 32, 
                                      mr: 1.5, 
                                      bgcolor: transaction.type === 'Recette' ? 'success.main' : 'error.main' 
                                    }}
                                  >
                                    {transaction.type === 'Recette' ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {transaction.reference}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {transaction.type === 'Recette' ? 'Recette' : 'Dépense'}
                          </Typography>
                                  </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                                  {new Date(transaction.date).toLocaleDateString('fr-FR')}
                        </Typography>
                          <Typography variant="caption" color="text.secondary">
                                  {transaction.methode}
                          </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {transaction.description}
                                </Typography>
                                {forfait && (
                                  <Chip 
                                    label={forfait.titre || forfait.forfaitNom} 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ mt: 0.5 }}
                                  />
                        )}
                      </TableCell>
                      <TableCell>
                                {client ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar 
                                      src={client.avatar} 
                                      sx={{ width: 24, height: 24, mr: 1 }}
                                    >
                                      {client.nom?.charAt(0)}
                                    </Avatar>
                                    <Typography variant="body2">
                                      {client.nom || transaction.clientNom}
                        </Typography>
                                  </Box>
                                ) : transaction.fournisseurNom ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar 
                                      sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}
                                    >
                                      <BusinessIcon fontSize="small" />
                                    </Avatar>
                                    <Typography variant="body2">
                                      {transaction.fournisseurNom}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2">-</Typography>
                                )}
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                                    color: transaction.type === 'Recette' ? 'success.main' : 'error.main' 
                          }}
                        >
                                  {transaction.type === 'Recette' ? '+' : '-'}{formatCurrency(transaction.devise !== 'DZD' ? transaction.montantDZD : transaction.montant)}
                        </Typography>
                                {transaction.devise !== 'DZD' && (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    ({formatCurrency(transaction.montant, transaction.devise)})
                                  </Typography>
                                )}
                                {transaction.commission > 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    Comm: {formatCurrency(transaction.commission)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.statut}
                          size="small"
                                  color={getStatusColor(transaction.statut)}
                                  icon={getStatusIcon(transaction.statut)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton 
                          size="small"
                              onClick={() => handleOpenDialog(transaction)}
                              color="primary"
                              aria-label="Modifier"
                        >
                              <EditIcon fontSize="small" />
                        </IconButton>
                            <IconButton 
                              size="small"
                              onClick={() => handleViewDetails(transaction)}
                              color="info"
                              aria-label="Voir détails"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small"
                              onClick={() => handlePrintReceipt(transaction)}
                              color="secondary"
                              aria-label="Imprimer reçu"
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small"
                              onClick={() => handleDeleteTransaction(transaction.id)}
                              color="error"
                              aria-label="Supprimer"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                          );
                        })
                    )}
              </TableBody>
            </Table>
          </TableContainer>
              
          <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredTransactions.length}
                rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
          />
        </Card>
      </TabPanel>



      {/* Removed popup menu in favor of inline action buttons */}

      {/* Enhanced Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {editingTransaction ? 'Modifier la transaction' : 'Nouvelle transaction'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Calculs automatiques avec taux de taxe: {settings.taxRate}%
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Recette">Recette</MenuItem>
                  <MenuItem value="Dépense">Dépense</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRangeIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={formData.categorie}
                  label="Catégorie"
                  onChange={(e) => setFormData({...formData, categorie: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Réservation">Réservation</MenuItem>
                  <MenuItem value="Fournisseur">Fournisseur</MenuItem>
                  <MenuItem value="Opérationnelle">Opérationnelle</MenuItem>
                  <MenuItem value="Marketing">Marketing</MenuItem>
                  <MenuItem value="Remboursement">Remboursement</MenuItem>
                  <MenuItem value="Autre">Autre</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Smart Client Selection */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => option.nom}
                value={clients.find(c => c.id === formData.clientId) || null}
                onChange={(event, newValue) => {
                  handleClientSelection(event, newValue);
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    {renderClientDropdownItem(option)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="👤 Client"
                    placeholder="Sélectionner un client..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonPinIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                freeSolo
                onInputChange={(event, newInputValue) => {
                  setFormData({ ...formData, client: newInputValue });
                }}
                fullWidth
              />
            </Grid>

            {/* Show client reservations if a client is selected and it's a revenue transaction */}
            {formData.clientId && formData.type === 'Recette' && formData.categorie === 'Réservation' && (
              <Grid item xs={12}>
                <Box sx={{ mt: 1, mb: 2 }}>
                  <ClientReservationsDropdown 
                    clientId={formData.clientId} 
                    onSelectReservation={handleReservationSelection} 
                  />
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Montant HT"
                    type="number"
                    value={formData.montant}
                    onChange={(e) => setFormData({...formData, montant: parseFloat(e.target.value)})}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Devise</InputLabel>
                    <Select
                      value={formData.devise}
                      label="Devise"
                      onChange={(e) => {
                        const newDevise = e.target.value;
                        const showConversion = newDevise !== 'DZD';
                        
                        // Get current exchange rate from settings
                        let tauxDeChange = null;
                        if (showConversion && settings.exchangeRates && settings.exchangeRates[newDevise]) {
                          tauxDeChange = settings.exchangeRates[newDevise].rate;
                        }
                        
                        setFormData({
                          ...formData, 
                          devise: newDevise,
                          showConversion: showConversion,
                          tauxDeChange: tauxDeChange
                        });
                      }}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="DZD">DZD</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Currency conversion section - simplified */}
            {formData.showConversion && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(124, 58, 237, 0.04)', borderRadius: 2, border: '1px solid rgba(124, 58, 237, 0.2)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#7c3aed' }}>
                    🔄 Conversion Automatique ({formData.devise} → DZD)
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2">
                          Taux de change: <b>1 {formData.devise} = {formData.tauxDeChange || '?'} DZD</b>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Défini dans les paramètres
                          </Typography>
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1, bgcolor: 'primary.light', color: 'white' }}>
                        <Typography variant="caption">Montant équivalent</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(formData.montant * (formData.tauxDeChange || 0))}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* Real-time Tax Calculator */}
              <Grid item xs={12}>
              <Box sx={{ mt: 2, mb: 2 }}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(46, 125, 50, 0.04)', borderRadius: 2, border: '1px solid rgba(46, 125, 50, 0.1)' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: 'success.main' }}>
                    💰 Calcul Automatique des Taxes et Commissions
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Montant HT</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {formData.devise !== 'DZD' 
                            ? `${formatCurrency(calculatedValues.montantHT, formData.devise)} (${formatCurrency(calculatedValues.montantDZD)})`
                            : formatCurrency(calculatedValues.montantHT)
                          }
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {formData.enableTVA ? `Taxes (${formData.customTVARate}%)` : 'Taxes (0%)'}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: formData.enableTVA ? 'warning.main' : 'text.disabled' }}>
                          {formatCurrency(calculatedValues.taxAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Montant TTC</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {formatCurrency(calculatedValues.totalAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {formData.enableCommission && formData.type === 'Recette' ? 
                            `Commission (${formData.customCommissionRate}%)` : 'Commission (0%)'}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: formData.enableCommission && formData.type === 'Recette' ? 'info.main' : 'text.disabled' }}>
                          {formatCurrency(calculatedValues.commissionAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
              </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Tax and Commission Controls */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
                  💰 Paramètres de calcul financier
                </Typography>
                
                <Grid container spacing={2}>
                  {/* TVA Controls */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.enableTVA}
                            onChange={(e) => setFormData({...formData, enableTVA: e.target.checked})}
                            color="primary"
                          />
                        }
                        label="Appliquer la TVA"
                      />
                    </Box>
                    
                    <TextField
                      label="Taux de TVA (%)"
                      type="number"
                      value={formData.customTVARate}
                      onChange={(e) => setFormData({...formData, customTVARate: Number(e.target.value)})}
                      disabled={!formData.enableTVA}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  
                  {/* Commission Controls - Only for revenue transactions */}
                  {formData.type === 'Recette' && (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.enableCommission}
                              onChange={(e) => setFormData({...formData, enableCommission: e.target.checked})}
                              color="secondary"
                            />
                          }
                          label="Calculer commission"
                        />
                      </Box>
                      
                      <TextField
                        label="Taux de commission (%)"
                        type="number"
                        value={formData.customCommissionRate}
                        onChange={(e) => setFormData({...formData, customCommissionRate: Number(e.target.value)})}
                        disabled={!formData.enableCommission}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Méthode de paiement</InputLabel>
                <Select
                  value={formData.methode}
                  label="Méthode de paiement"
                  onChange={(e) => setFormData({...formData, methode: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Virement bancaire">Virement bancaire</MenuItem>
                  <MenuItem value="Carte bancaire">Carte bancaire</MenuItem>
                  <MenuItem value="Espèces">Espèces</MenuItem>
                  <MenuItem value="Chèque">Chèque</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={formData.statut}
                  label="Statut"
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="En attente">En attente</MenuItem>
                  <MenuItem value="Terminé">Terminé</MenuItem>
                  <MenuItem value="Annulé">Annulé</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            Annuler
          </Button>
          <Button 
            onClick={handleSaveTransaction}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)'
            }}
          >
            {editingTransaction ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Dialog */}
      <Dialog 
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Détails de la Transaction
          </Typography>
        </DialogTitle>
        <DialogContent>
          {detailsData && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.05)', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    {detailsData.reference}
                  </Typography>
                  <Chip 
                    label={detailsData.type} 
                    color={detailsData.type === 'Recette' ? 'success' : 'error'} 
                    size="small" 
                    sx={{ mr: 1 }} 
                  />
                  <Chip 
                    label={detailsData.statut} 
                    color={getStatusColor(detailsData.statut)} 
                    size="small" 
                  />
                </Paper>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Date</Typography>
                <Typography variant="body2">{detailsData.date}</Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Catégorie</Typography>
                <Typography variant="body2">{detailsData.categorie}</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body2">{detailsData.description}</Typography>
              </Grid>
              
              {detailsData.client && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Client</Typography>
                  <Typography variant="body2">{detailsData.client}</Typography>
                </Grid>
              )}
              
              {detailsData.reservation && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Réservation</Typography>
                  <Typography variant="body2">{detailsData.reservation}</Typography>
                </Grid>
              )}
              
              {detailsData.fournisseur && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Fournisseur</Typography>
                  <Typography variant="body2">{detailsData.fournisseur}</Typography>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Montant HT</Typography>
                <Typography variant="body2">{detailsData.montantHT}</Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Taxes</Typography>
                <Typography variant="body2">{detailsData.taxes}</Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Montant TTC</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{detailsData.montantTTC}</Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Commission</Typography>
                <Typography variant="body2">{detailsData.commission}</Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Méthode de paiement</Typography>
                <Typography variant="body2">{detailsData.methode}</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                <Typography variant="body2">{detailsData.notes}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>
            Fermer
          </Button>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />}
            onClick={() => {
              setOpenDetailsDialog(false);
              // Create transaction object from detailsData
              const transaction = {
                id: detailsData.id,
                reference: detailsData.reference,
                date: detailsData.date,
                type: detailsData.type,
                categorie: detailsData.categorie,
                statut: detailsData.statut,
                methode: detailsData.methode,
                notes: detailsData.notes
              };
              handlePrintReceipt(transaction);
            }}
          >
            Imprimer reçu
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
};

export default Finances; 